/* ==========================================================================
   AMALGAM — Collection card "Price on application" swap
   --------------------------------------------------------------------------
   Native Horizon product cards render a zero price as "£0.00" (or the locale
   equivalent). This enhancement finds those, confirms the price is genuinely
   zero (locale-proof), and swaps the text for a POA label.

   WHY JS, NOT CSS: CSS cannot read the price value — a £0.00 <span> is
   structurally identical to a £6,995.00 one, so :has()/selectors can't tell
   them apart. WHY JS, NOT NATIVE LIQUID: this reads the card's rendered output
   and rewrites text only; it does NOT touch the native card template, so it
   survives Horizon theme updates.

   SCOPE: collection / search / any page that renders <product-price>. Safe to
   load globally — it no-ops where there are no product cards.

   NON-INVASIVE: reads native output, edits text nodes only. No native file edit.
   ========================================================================== */

(function () {
  'use strict';

  /* --- POA label ----------------------------------------------------------
     The label is now rendered server-side in the active locale via `| t` and
     read from <body data-poa-label> (set in layout/theme.liquid from
     'product.price_on_application'). Copy lives in Shopify's locale files, so
     translators own it and ZH-CN / ZH-TW are distinct (the old in-JS map
     collapsed both to Simplified via .split('-')). Falls back to English. */
  function poaLabel() {
    var label = (document.body && document.body.getAttribute('data-poa-label')) || '';
    return label.trim() || 'Price on application';
  }

  /* --- Locale-proof zero test -------------------------------------------
     Strip everything that isn't a digit, then check the remainder is all
     zeros. Survives every currency format because it ignores symbols,
     thousands separators and decimal marks:
       £0.00 -> "000" -> zero      €0,00 -> "000" -> zero
       ¥0    -> "0"   -> zero      £6,995.00 -> "699500" -> not zero
     A value with NO digits (e.g. the word "Free") returns false — we leave
     those alone rather than guess. */
  function isZeroMoney(text) {
    var digits = (text || '').replace(/[^0-9]/g, '');
    return digits.length > 0 && /^0+$/.test(digits);
  }

  function swap(priceEl) {
    if (priceEl.getAttribute('data-poa-applied') === 'true') return;

    // .money comes from the store's GBP money format only; other Markets
    // currencies render bare text in .price, so fall back to it.
    var moneyEl = priceEl.querySelector('.money') || priceEl.querySelector('.price');
    if (!moneyEl) return;
    if (!isZeroMoney(moneyEl.textContent)) return;

    // Replace the money text with the POA label. We rewrite the .money span's
    // text so it inherits the card's existing price typography/colour.
    moneyEl.textContent = poaLabel();
    priceEl.setAttribute('data-poa-applied', 'true');
    priceEl.classList.add('is-poa'); // hook for optional CSS styling
  }

  function run(root) {
    (root || document).querySelectorAll('product-price').forEach(swap);
  }

  /* Initial pass */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { run(); });
  } else {
    run();
  }

  /* --- Re-run after dynamic updates --------------------------------------
     Collection filtering, "load more", predictive search and Markets
     currency switches re-render cards. A MutationObserver re-applies the swap
     to any newly-injected <product-price>. Debounced so a burst of DOM
     changes triggers one pass, not dozens. */
  var pending = false;
  var observer = new MutationObserver(function () {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      run();
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
